import { useMutation } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';

import { evictCache } from '@neo.tax/shared/apollo';
import {
  AccountingProvider,
  GET_PRIOR_YEAR_REVENUES,
  MergeDevAccountingProvider,
  SYNC_ACCOUNTING_PROVIDER,
  SyncAccountingProviderMutation,
  SyncAccountingProviderMutationVariables,
  GetPriorYearRevenuesQueryVariables,
  GET_FISCAL_YEAR,
  GetFiscalYearQueryVariables,
} from '@neo.tax/shared/graphql';
import { useCurrentCompanyId, useCurrentUser, useMergeDevAccounting } from '@neo.tax/shared/hooks';
import { getQboDataLinkingAuthUrl } from '@neo.tax/shared/third-parties';
import { getPathUrl, Path } from '@neo.tax/shared/utils';

import { ConnectProviderStatus } from '../enums';
import { UseProviderConnection } from '../types';
import { useProviderConnectState } from './useProviderConnectState';

export const useSyncAccountingProvider = ({
  fiscalYearId,
  providerName,
}: {
  fiscalYearId: string;
  providerName: AccountingProvider;
}): UseProviderConnection => {
  const companyId = useCurrentCompanyId();
  const { user } = useCurrentUser();

  const [connectStatus, setConnectStatus] = useState<ConnectProviderStatus>(ConnectProviderStatus.NOT_STARTED);

  const [syncAccountingProvider] = useMutation<SyncAccountingProviderMutation, SyncAccountingProviderMutationVariables>(
    SYNC_ACCOUNTING_PROVIDER,
    {
      variables: {
        fiscalYearId,
        provider: providerName,
      },
      awaitRefetchQueries: true,
      update: (cache) => {
        // invalidate getAccountingQres and getAccountingQreSummary cache
        evictCache(cache, ['getAccountingQres', 'getAccountingQreSummary']);
      },
      refetchQueries: [
        {
          query: GET_FISCAL_YEAR,
          variables: {
            fiscalYearId,
          } satisfies GetFiscalYearQueryVariables,
        },
        {
          query: GET_PRIOR_YEAR_REVENUES,
          variables: {
            companyId,
          } satisfies GetPriorYearRevenuesQueryVariables,
        },
      ],
    },
  );

  const syncAccounting = useCallback(async (): Promise<void> => {
    try {
      setConnectStatus(ConnectProviderStatus.SYNCING);
      await syncAccountingProvider();
      setConnectStatus(ConnectProviderStatus.SUCCESS);
    } catch (err) {
      console.error(err);
      setConnectStatus(ConnectProviderStatus.ERROR);
    }
  }, [syncAccountingProvider]);

  const { onOpen: openNetsuite } = useMergeDevAccounting({
    provider: MergeDevAccountingProvider.Netsuite,
    isParentReady: connectStatus === ConnectProviderStatus.WAITING_TOKEN,
    companyId,
    onSuccess: syncAccounting,
    onError: () => setConnectStatus(ConnectProviderStatus.ERROR),
  });

  const onQuickbooksFail = (): void => {
    stopPolling();
    setConnectStatus(ConnectProviderStatus.ERROR);
  };

  const { startPolling, stopPolling, tokenRegistered } = useProviderConnectState({
    fiscalYearId,
    providerName,
    providerType: 'accounting',
    onFail: onQuickbooksFail,
  });

  const openQuickbooks = (): void => {
    const authUrl = getQboDataLinkingAuthUrl({
      userId: user?.id || '',
      companyId,
      successRedirectUrl: getPathUrl({ path: Path.PAYROLL_QRE, companyId, fiscalYearId }),
      errorRedirectUrl: getPathUrl({ path: Path.PAYROLL_QRE, companyId, fiscalYearId }),
      isSso: false,
    });
    startPolling(1000);
    setConnectStatus(ConnectProviderStatus.WAITING_TOKEN);
    window.open(authUrl, '_blank');
  };

  const handleOpenNetsuite = (): void => {
    setConnectStatus(ConnectProviderStatus.WAITING_TOKEN);
    openNetsuite();
  };

  const openAccountingConnection = (): void => {
    switch (providerName) {
      case AccountingProvider.Quickbooks:
        openQuickbooks();
        return;
      case AccountingProvider.Netsuite:
        handleOpenNetsuite();
        return;
      default:
        return;
    }
  };

  useEffect(() => {
    if (connectStatus !== ConnectProviderStatus.WAITING_TOKEN || !tokenRegistered) {
      return;
    }

    stopPolling();
    syncAccounting();
  }, [stopPolling, syncAccounting, providerName, connectStatus, tokenRegistered]);

  const connect = (): void => {
    // WIP treat netsuite like Intuit SSO where a token already exists
    if (tokenRegistered) {
      syncAccounting();
    } else {
      openAccountingConnection();
    }
  };

  return {
    connect,
    connectStatus,
  };
};
